import { useActivity } from 'ia-react-core';
import { FC, useContext, useMemo, useRef } from 'react';
import { ApplicationAvailabilityContext, AuthenticationContext, Availability } from 'omni-package-react-core';
import { getOmniLogger } from 'omni-package-react-logging';
import { useLocation } from 'react-router-dom';
import getTokenManager from '~/utils/getTokenManager';
import { getAvailabilityConfiguration } from '~/utils/getAvailabilityConfiguration';
import { refreshCurrentSession } from './refreshCurrentSession';
import { validateAvailability } from './validateAvailability';
import { refreshToken } from './refreshToken';
import { checkVersionAndReload } from '../ApplicationVersioningResource/ApplicationVersioningResource';

export interface ActivityManagerProps { }

const oneMinute = 60000;
const fiveMinutes = 300000;
const versionCheckCooldownMs = 5000;

const shouldRequestVersion = (lastVersionCheckAt: number | null): { shouldRequest: boolean; now: number } => {
  const now = Date.now();

  if (lastVersionCheckAt === null) {
    return { shouldRequest: true, now };
  }

  if (now - lastVersionCheckAt < versionCheckCooldownMs) {
    return { shouldRequest: false, now };
  }

  return { shouldRequest: true, now };
};

const ActivityManager: FC<ActivityManagerProps> = () => {
  const { authenticationInfo } = useContext(AuthenticationContext);
  const availabilityCtx = useContext(ApplicationAvailabilityContext);
  const logger = getOmniLogger('we-client-space-activity-manager');
  const tokenManager = useMemo(() => getTokenManager(), []);
  const location = useLocation();
  const config = useMemo(() => getAvailabilityConfiguration(), []);
  const lastVersionCheckAt = useRef<number | null>(null);

  useActivity(() => {
    const { shouldRequest, now } = shouldRequestVersion(lastVersionCheckAt.current);

    if (shouldRequest) {
      lastVersionCheckAt.current = now;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      checkVersionAndReload();
    }
    refreshToken(authenticationInfo.isAuthenticationProcessed, logger, tokenManager);
    validateAvailability(
      authenticationInfo.isAuthenticationProcessed,
      config.group,
      config.company,
      config.experience,
      (av: Availability) => availabilityCtx.saveAvailability(av),
    );
  }, [authenticationInfo.isAuthenticationProcessed], { frequency: oneMinute, strategy: 'leading' });

  useActivity(() => {
    refreshCurrentSession(authenticationInfo.isAuthenticationProcessed, logger, location);
  }, [authenticationInfo.isAuthenticationProcessed], { frequency: fiveMinutes, strategy: 'leading' });

  return null;
};

export default ActivityManager;
